import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Inline, Stack, Text } from "braid-design-system";
import { toStaticResourceUrl } from "../../utils/resourceUrl";

// Same limit the backend enforces for images in app/utils/file_upload.py.
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

interface GroupImageFieldProps {
    label: string;
    description?: string;
    /** Saved path (e.g. "uploads/image/GRP_x.jpg"), or "" when none. */
    currentPath: string;
    /** File picked but not yet uploaded — the drawer uploads it on save. */
    selectedFile: File | null;
    disabled?: boolean;
    onSelect: (file: File | null) => void;
    onRemove: () => void;
}

/** Image picker for an item group's marketplace tile, previewed at the tile's shape. */
const GroupImageField: React.FC<GroupImageFieldProps> = ({
    label,
    description,
    currentPath,
    selectedFile,
    disabled = false,
    onSelect,
    onRemove,
}) => {
    const { t } = useTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [objectUrl, setObjectUrl] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!selectedFile) {
            setObjectUrl(null);
            return;
        }
        const url = URL.createObjectURL(selectedFile);
        setObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [selectedFile]);

    const previewUrl = objectUrl ?? toStaticResourceUrl(currentPath);

    const handleFile = (file: File | null) => {
        setError(null);
        if (file && !file.type.startsWith("image/")) {
            setError(t("page.item_group_management.drawer.validation.image_type", { defaultValue: "Choose an image file" }));
            return;
        }
        if (file && file.size > MAX_IMAGE_BYTES) {
            setError(t("page.item_group_management.drawer.validation.image_size", { defaultValue: "Image must be 20MB or smaller" }));
            return;
        }
        onSelect(file);
    };

    return (
        <Stack space="small">
            <Text size="standard" weight="strong">{label}</Text>
            {description ? <Text size="small" tone="secondary">{description}</Text> : null}

            <Box
                style={{
                    width: 132,
                    height: 112,
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "#f3f4f6",
                    border: "1px dashed #d1d5db",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt={label}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                ) : (
                    <Text size="xsmall" tone="secondary" align="center">
                        {t("page.item_group_management.drawer.placeholder.group_image", { defaultValue: "No image" })}
                    </Text>
                )}
            </Box>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                disabled={disabled}
                onChange={(e) => {
                    handleFile(e.target.files?.[0] ?? null);
                    e.target.value = "";
                }}
            />

            {!disabled && (
                <Inline space="small">
                    <Button size="small" variant="soft" onClick={() => inputRef.current?.click()}>
                        {previewUrl
                            ? t("page.item_group_management.drawer.button.replace_image", { defaultValue: "Replace" })
                            : t("page.item_group_management.drawer.button.choose_image", { defaultValue: "Choose image" })}
                    </Button>
                    {previewUrl ? (
                        <Button size="small" variant="transparent" tone="critical" onClick={onRemove}>
                            {t("page.item_group_management.drawer.button.remove_image", { defaultValue: "Remove" })}
                        </Button>
                    ) : null}
                </Inline>
            )}

            {error ? <Text size="small" tone="critical">{error}</Text> : null}
        </Stack>
    );
};

export default GroupImageField;
